import { useState, useCallback, useRef } from 'react';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { delayHeavyTask } from 'client/site-modules/shared/utils/inp-utils';
import { useAppraisalTabsState } from 'site-modules/shared/components/appraisal/appraisal-tabs-v2/context/appraisal-tabs-context';

/**
 * Custom hook encapsulating MMY form submission logic.
 *
 * @param {Object} params
 * @param {string} [params.creativeId] - Creative ID passed through to the tracking event.
 * @returns {{ validate: Function, validated: boolean, resetValidation: Function }}
 */
export function useMmySubmit({ creativeId } = {}) {
  const { mmy, setVin, setStyleIds, setIsReadyForRedirect } = useAppraisalTabsState();

  const [validated, setValidated] = useState(false);

  // Stale-closure guard: lets `validate` read the latest `mmy` without
  // being re-created on every keystroke (which would force submit-button re-renders).
  const mmyRef = useRef(mmy);
  // eslint-disable-next-line react-hooks/refs -- stable event handlers require the latest MMY value.
  mmyRef.current = mmy;

  const isMmyPresent = useCallback(() => {
    const currentMmy = mmyRef.current;
    return !!(currentMmy?.make && currentMmy?.model && currentMmy?.year);
  }, []);

  const resetValidation = useCallback(() => {
    setValidated(false);
  }, []);

  const validate = useCallback(
    e => {
      e.preventDefault();
      setValidated(true);

      if (!isMmyPresent()) return;

      setValidated(false);

      delayHeavyTask(() => {
        setVin('');
        setStyleIds([]);
        setIsReadyForRedirect(!!isMmyPresent());
      });

      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_END,
        event_data: {
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          action_name: TrackingConstant.ACTION_APPRAISE_VEHICLE,
          action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
          subaction_name: TrackingConstant.SUBMIT_VEHICLE,
          creative_id: creativeId,
          value: TrackingConstant.YMM,
        },
      });
    },
    [creativeId, isMmyPresent, setVin, setStyleIds, setIsReadyForRedirect]
  );

  return { validate, validated, resetValidation };
}
